import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { DemoProvider } from './context/DemoContext';
import { useDomainDetection } from './hooks/useDomainDetection';

// Import AiQ App (unified TSX platform)
import AiQApp from './aiq/AiQApp';

// Import Login
import LoginPage from './components/LoginPage';
import WelcomePage from './components/NewWelcomePage';
import LandingPage from './components/LandingPage';
import QuantClawLanding from './components/QuantClawLanding';
import TalkoruPasswordModal from './components/TalkoruPasswordModal';

import './App.css';

function App() {
  const {
    currentUser,
    isInitializing,
    talkoruNeedsPassword,
    talkoruTokenInfo,
    talkoruProcessing,
    talkoruError,
    talkoruEmailEditable,
    completeTalkoruLogin,
    cancelTalkoruLogin
  } = useAuth();
  const appDomain = useDomainDetection();
  const isQuantClaw = appDomain === 'quantclaw';

  // Check if there's a talkoru_token in URL - must block routing until processed
  const hasTalkoruToken = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('talkoru_token');

  // Show loading spinner while:
  // - checking auth (isInitializing)
  // - talkoru token exists but password modal hasn't been triggered yet
  const waitingForTalkoruToken = hasTalkoruToken && !talkoruNeedsPassword && !talkoruError;

  if (isInitializing || waitingForTalkoruToken) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50 text-slate-500">
        <div className="flex flex-col items-center gap-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="text-sm font-medium">
            {waitingForTalkoruToken ? 'Processing login...' : 'Initializing...'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <Router>
      {/* Talkoru Password Modal - shown when user arrives via Talkoru magic link */}
      {talkoruNeedsPassword && talkoruTokenInfo && (
        <TalkoruPasswordModal
          tokenInfo={talkoruTokenInfo}
          isProcessing={talkoruProcessing}
          error={talkoruError}
          onSubmit={completeTalkoruLogin}
          onCancel={cancelTalkoruLogin}
          emailEditable={talkoruEmailEditable}
        />
      )}

      <Routes>
        {/* Landing Page - Show QuantClaw for quantclaw.ai domain */}
        <Route
          path="/"
          element={
            currentUser
              ? <Navigate to="/app" replace />
              : isQuantClaw
                ? <QuantClawLanding />
                : <LandingPage />
          }
        />

        {/* Login Route */}
        <Route 
          path="/login" 
          element={!currentUser ? <LoginPage /> : <Navigate to="/app" replace />} 
        />
        
        {/* Welcome Page */}
        <Route 
          path="/welcome" 
          element={currentUser ? <WelcomePage /> : <Navigate to="/login" replace />} 
        />

        {/* Demo Route - No Authentication Required */}
        <Route 
          path="/demo/*" 
          element={
            <DemoProvider isDemoMode={true}>
              <div className="fixed inset-0 z-[9999] bg-slate-900 overflow-hidden">
                <AiQApp isDemoMode={true} />
              </div>
            </DemoProvider>
          } 
        />

        {/* Main App Route - AiQ Platform (Unified TSX)
            Public tools are accessible without login; personal data and AI
            features show a gate screen inside AiQApp. */}
        <Route
          path="/app/*"
          element={
            <DemoProvider isDemoMode={false}>
              <div className="fixed inset-0 z-[9999] bg-slate-900 overflow-hidden">
                <AiQApp isDemoMode={false} />
              </div>
            </DemoProvider>
          }
        />

        {/* Redirect root to app */}
        <Route 
          path="*" 
          element={
            currentUser 
              ? <Navigate to="/app" replace /> 
              : <Navigate to="/" replace /> 
          } 
        />
      </Routes>
    </Router>
  );
}

export default App;

